<template>
  <div v-if="authorized">
    <BushelBtn
      :class="btnClass"
      :variant="newMessagesCount && newMessagesCount > 0 ? variantMessages : variant"
      :outline="outline ? (flat ? false : showingChatWindow ? false : true) : false"
      :btn-style="
        (flat
          ? ''
          : outline
            ? newMessagesCount && newMessagesCount > 0
              ? `border: 1px solid ${outlineBorderColorMessages};`
              : `border: 1px solid ${outlineBorderColor};`
            : '') + btnStyle
      "
      :flat="flat ? (showingChatWindow ? false : true) : false"
      :ripple="false"
      :button-id="btnId"
      :tooltip-message="btnTooltip"
      :tooltip-placement="btnTooltipPlacement"
      @click="triggerChatWindow"
    >
      <span class="position-relative">
        <i class="far fa-comments fa-fw" style="font-size: 20px; line-height: 1; margin: -10px 0 -10px 0" :style="iconStyle" :class="iconClass" />
        <BBadge
          v-if="newMessagesCount && newMessagesCount > 0"
          class="badge position-absolute badge-pill pl-1 pr-1 pt-1"
          :variant="showingChatWindow ? (outline ? 'light' : variantMessages) : outline ? variantMessages : 'light'"
          :class="showingChatWindow ? (outline ? 'text-' + variantMessages : 'text-white') : outline ? 'text-white' : 'text-' + variantMessages"
          style="left: -5px; top: 5px; font-size: 8px"
        >
          <span>
            {{ newMessagesCount }}
          </span>
        </BBadge>
      </span>
      {{ showButtonText ? buttonText : "" }}
    </BushelBtn>

    <BModal
      v-if="authorized && showingChatWindow"
      id="companyProfileChatWindow"
      :visible="showingChatWindow"
      scrollable
      no-close-on-backdrop
      :size="modalSize()"
      body-class="p-0 border-0 shadow-none"
      @hide="closeChatWindow"
    >
      <template #modal-header>
        <div class="d-flex w-100 single-page-element-position max-w-100">
          <div class="my-auto text-center pl-0">
            <h5 class="text-primary">Chat with {{ company.name }}</h5>
          </div>
          <div class="ml-auto text-center pr-0" style="margin-top: -3px">
            <BushelBtn
              v-if="allowFullScreenToggle"
              variant="secondary"
              flat
              btn-container-class="btn-xs modal-close mr-1"
              button-id="expandModal"
              :tooltip-message="showingFullScreen ? 'Compress to Regular Screen' : 'Expand to Full Screen'"
              tooltip-placement="left"
              style="margin-top: -5px"
              @click="showingFullScreen = !showingFullScreen"
            >
              <i :class="showingFullScreen ? 'fas fa-compress' : 'fas fa-expand'" />
            </BushelBtn>
            <button type="button" class="modal-close" aria-label="Close" :disabled="submitting ? true : false" @click="closeChatWindow()">
              <i
                :class="submitting ? 'fas fa-circle-notch fa-spin' : 'fa fa-times-thin'"
                :style="submitting ? 'font-size: 1.05em; margin-top: 5px;' : 'font-size: 1.5em; margin-top: 0px;'"
              />
            </button>
          </div>
        </div>
      </template>
      <div v-if="!chatReady">
        <div class="text-center mt-5">
          <ChatLoadingDots class="mt-5" />
        </div>
      </div>
      <div v-if="chatReady">
        <div v-if="chatFound" class="overflow-hidden">
          <ChatDialog
            :key="'public_chat_d_' + chatFound.uuid"
            :initial-chat-message="newChatMessage"
            :active-chat="chatFound"
            :full-active-chat="chatFound"
            :loading="false"
            :user="user"
            :data-ready="chatReady"
            :invoices-available="invoicesAvailable"
            :invoices-loaded="invoicesLoaded"
            :loading-invoices="loadingInvoices"
            @messages-updated="messagesUpdated"
          />
        </div>
        <div v-if="!chatFound">
          <div class="text-center" style="padding: 46px 0 46px 0">
            <i class="far fa-comment-dots fa-5x text-light p-5 m-4" />
          </div>
          <NewChatMessage
            :initial-chat-message="newChatMessage"
            :error="submitted && !newChatMessage && newChatImages.length == 0 && newChatInvoices.length == 0 ? true : false"
            :submitted="submitted"
            :submitting="submitting"
            :loading="false"
            :invoices-available="invoicesAvailable"
            :invoices-loaded="invoicesLoaded"
            :loading-invoices="loadingInvoices"
            :clear-dialog="refresh"
            :disabled="submitting"
            new-chat
            @send-message="startNewChatAndMessage"
          />
        </div>
      </div>

      <template #modal-footer>
        <BushelBtn :disabled="submitting" variant="dark" ripple-color="dark" flat @click="closeChatWindow()"> Close </BushelBtn>
      </template>
    </BModal>
  </div>
</template>

<script>
import BBadge from "@/components/base/BBadge.vue";
import * as websockets from "@/services/websockets";
import * as api from "@/services/api";
import getAvailableInvoicesForChat from "@/services/chat/chat";
import ChatDialog from "@/components/chat/ChatDialog.vue";
import ChatLoadingDots from "@/components/chat/ChatLoadingDots.vue";
import NewChatMessage from "@/components/chat/NewChatMessage.vue";

export default {
  name: "CompanyChatWindowWithTrigger",
  components: {
    BBadge,
    ChatDialog,
    ChatLoadingDots,
    NewChatMessage,
  },
  props: {
    initialChatMessage: { type: String },
    authorized: { type: Boolean, default: false },
    user: { type: Object, required: true },
    company: { type: Object, required: true },
    allowFullScreenToggle: { type: Boolean, default: true },
    showButtonText: { type: Boolean, default: true },
    buttonText: { type: String, default: "Chat Now" },
    btnStyle: { type: String, default: null },
    btnClass: { type: String, default: "btn-sm d-none d-sm-inline-block" },
    iconStyle: { type: String, default: null },
    iconClass: { type: String, default: null },
    flat: { type: Boolean, default: false },
    outline: { type: Boolean, default: true },
    variant: { type: String, default: "primary" },
    variantMessages: { type: String, default: "warning" },
    outlineBorderColor: { type: String, default: "#40516A" },
    outlineBorderColorMessages: { type: String, default: "#ffc107" },
    otherSide: { type: String, default: "buyer" },
    btnId: { type: String },
    btnTooltip: { type: String },
    btnTooltipPlacement: { type: String, default: "bottom" },
    checkForUnread: { type: Boolean, default: false },
  },
  emits: ["chatMessagesUpdated", "chatWindowOpen"],
  data() {
    return {
      showingChatWindow: false,
      newMessagesCount: 0,
      chatWindowKey: 957430,
      showingFullScreen: false,
      chatReady: false,
      chatFound: null,
      refresh: false,
      submitting: false,
      submitted: false,
      newChatMessage: null,
      newChatImages: [],
      newChatInvoices: [],
      invoicesAvailable: [],
      loadingInvoices: false,
      invoicesLoaded: false,
    };
  },
  computed: {
    notificationsChannelName() {
      if (this.user && this.user.current_company_uuid) {
        return `company-notifications.${this.user.current_company_uuid}`;
      }
      return null;
    },
  },
  watch: {},
  created() {},
  mounted() {
    this.setupChat();
  },
  beforeUnmount() {
    if (this.authorized) {
      this.leaveChatChannel();
      this.letAppKnowChatClose();
    }
  },
  updated() {},
  methods: {
    getAvailableInvoicesForChat,
    modalSize() {
      if (this.showingFullScreen) {
        return "full";
      }
      return "lg";
    },
    async triggerChatWindow() {
      if (!this.checkForUnread) {
        await this.checkCompanyChatNotifications();
      }
      this.getAvailableInvoices();
      this.chatWindowKey += 1;
      this.showingChatWindow = true;
      this.letAppKnowChatOpen();
    },
    closeChatWindow() {
      this.showingChatWindow = false;
      this.showingFullScreen = false;
      this.leaveChatChannel();
      this.letAppKnowChatClose();
    },
    messagesUpdated(val) {
      this.$emit("chatMessagesUpdated", val);
    },
    async setupChat() {
      if (this.authorized) {
        if (this.checkForUnread) {
          await this.checkCompanyChatNotifications();
        }
        if (this.initialChatMessage) {
          this.newChatMessage = this.initialChatMessage ? this.initialChatMessage : "";
        }
        // await this.runCompanyNotificationsListener();
        // await this.getAvailableInvoices();
        this.chatReady = true;
      }
    },
    async checkCompanyChatNotifications() {
      const self = this;
      const buyerCompanyId = this.user.current_company_id;
      const vendorCompanyId = this.company.id;
      const data = {
        buyerCompanyId,
        vendorCompanyId,
      };
      await api.post("company-chat/storefront-chat-unread-notifications", data).then(
        (response) => {
          if (response && response.data && response.data.code == 200) {
            if (
              self.chatFound &&
              self.chatFound.id &&
              response.data.data &&
              response.data.data.chatRoom &&
              response.data.data.chatRoom.id &&
              self.chatFound.id != response.data.data.chatRoom.id
            ) {
              self.chatFound = response.data.data.chatRoom;
            }
            if (!self.chatFound || self.chatFound == null) {
              self.chatFound = response.data.data.chatRoom;
            }
            self.newMessagesCount = 0;
            if (response.data.data.chatRoom && response.data.data.chatRoom.chat_messages) {
              self.newMessagesCount = response.data.data.chatRoom.chat_messages.length;
            }
          } else {
            // self.setSnackbar({ variant: 'danger', title: 'Error', message: 'Error checking chat notifications' });
          }
        },
        (error) => {
          // self.setSnackbar({ variant: 'danger', title: 'Error', message: 'Error checking chat notifications' });
        },
      );
    },
    async runCompanyNotificationsListener(oldUuid = null) {
      const self = this;
      if (oldUuid) {
        await window.Echo.leave(`company-notifications.${oldUuid}`);
      }
      if (self.notificationsChannelName) {
        //   // await self.leaveNotificationsChannel(); // Dont leave it, the sidebar and header might be using it...
        await window.Echo.private(self.notificationsChannelName).listen("CompanyNotificationUpdated", (event) => {
          if (event.type == "chat-message") {
            self.handleCompanyNotification(event);
          }
        });
      }
    },
    async handleCompanyNotification(event) {
      const self = this;
      if (event && event.data && event.data.roomUuid) {
        if (self.chatFound && self.chatFound.uuid) {
          if (self.chatFound.uuid == event.data.roomUuid) {
            await self.checkCompanyChatNotifications();
          } else {
            //
          }
        } else {
          await self.checkCompanyChatNotifications();
        }
      } else {
        // self.checkCompanyChatNotifications();
      }
    },
    async startedNewChatAndMessage(data) {
      if (this.initialChatMessage) {
        this.newChatMessage = this.initialChatMessage ? this.initialChatMessage : "";
      }
      const self = this;
      this.submitting = true;
      await api.post("company-chat/start-chat", data).then(
        (response) => {
          if (response && response.data && response.data.code == 201) {
            const chat = response.data.data.chatRoom;
            self.chatFound = chat;
            self.checkCompanyChatNotifications();
            self.refreshChat();
          } else {
            // self.setSnackbar({ variant: 'danger', title: 'Error', message: 'Error creating new company chat' });
          }
        },
        (error) => {
          // self.setSnackbar({ variant: 'danger', title: 'Error', message: 'Error creating new company chat' });
        },
      );
      this.submitting = false;
      self.checkCompanyChatNotifications();
    },
    async getAvailableInvoices() {
      if (!this.company || !this.user) {
        return false;
      }
      let buyerCompanyId = this.user.current_company_id;
      let vendorCompanyId = this.company.id;
      let requestorType = "buyer";

      if (this.otherSide == "vendor") {
        buyerCompanyId = this.company.id;
        vendorCompanyId = this.user.current_company_id;
        requestorType = "vendor";
      }

      this.loadingInvoices = true;
      const response = await this.getAvailableInvoicesForChat({
        buyerCompanyId,
        sellerCompanyId: vendorCompanyId,
        requestorType,
        requestorCompanyId: this.user.current_company_id,
      });
      if (response && response.data && response.data.code == 200 && response.data.data && response.data.data.invoices) {
        this.invoicesAvailable = response.data.data.invoices;
        this.invoicesLoaded = true;
      } else {
        //   this.setSnackbar({ variant: 'danger', title: 'Error', message: 'Error getting chat invoices' });
      }
      this.loadingInvoices = false;
      return true;
    },
    async leaveChatChannel() {
      if (this.chatFound && this.chatFound.uuid) {
        await window.Echo.leave(`chat.${this.chatFound.uuid}`);
      }
    },
    refreshChat() {
      if (this.initialChatMessage) {
        this.newChatMessage = this.initialChatMessage ? this.initialChatMessage : "";
      }
      const self = this;
      self.newChatMessage = null;
      self.newChatImages = [];
      self.newChatInvoices = [];
      self.submitted = false;
      self.submitting = false;
      self.refresh = true;
      setTimeout(() => {
        self.refresh = false;
      }, 1);
    },
    startNewChatAndMessage(incoming) {
      if (this.initialChatMessage) {
        this.newChatMessage = this.initialChatMessage ? this.initialChatMessage : "";
      }
      const toCompanyId = this.company.id;
      const currentCompanyId = this.user.current_company_id;
      this.newChatMessage = incoming.message;
      this.newChatImages = incoming.images;
      this.newChatInvoices = incoming.invoices;
      this.submitted = true;
      if (
        toCompanyId &&
        currentCompanyId &&
        (this.newChatMessage || (this.newChatImages && this.newChatImages.length > 0) || (this.newChatInvoices && this.newChatInvoices.length > 0))
      ) {
        this.startedNewChatAndMessage({
          userId: this.user.id,
          currentCompanyId,
          toCompanyId,
          newMessage: this.newChatMessage,
          attachments: {
            images: this.newChatImages,
            invoices: this.newChatInvoices,
          },
        });
      }
    },
    letAppKnowChatOpen() {
      this.$root.$emit("chatWindowOpen", true);
    },
    letAppKnowChatClose() {
      this.$root.$emit("chatWindowOpen", false);
    },
  },
};
</script>

<style scoped></style>

<style lang="scss" scoped></style>
